import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Heart, Mail, Lock, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { authApi } from '../../lib/api/auth';
import { toast } from 'sonner';

interface ForgotPasswordScreenProps {
  onBackToLogin: () => void;
}

export function ForgotPasswordScreen({ onBackToLogin }: ForgotPasswordScreenProps) {
  const [step, setStep] = useState<'email' | 'code' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string>(''); // For demo purposes

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      const errorMsg = 'Please enter your email address';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.forgotPassword(email);
      
      // For demo, store the generated code (remove in production)
      if (response.reset_code) {
        setGeneratedCode(response.reset_code);
      }
      
      toast.success('Password reset code sent! Check your email.');
      setStep('code');
    } catch (error: any) {
      let errorMessage = 'Failed to send reset code. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!resetCode || !newPassword || !confirmPassword) {
      const errorMsg = 'Please fill in all fields';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (resetCode.length !== 6) {
      const errorMsg = 'Reset code must be 6 digits';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (newPassword.length < 6) {
      const errorMsg = 'Password must be at least 6 characters';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (newPassword !== confirmPassword) {
      const errorMsg = 'Passwords do not match';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      await authApi.resetPassword(email, resetCode, newPassword);
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (error: any) {
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Header */}
      <div className="bg-[#D4F0F8] p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mx-auto">
            <Heart className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-primary">Pulmonology Spectrum by Healthkon</h1>
          <p className="text-muted-foreground">Anytime – Anywhere – Healthcare</p>
        </div>
      </div>

      {/* Form */}
      <div className="p-6 max-w-md mx-auto">
        <Button
          variant="ghost"
          onClick={onBackToLogin}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Button>

        <Card className="shadow-lg">
          {step === 'email' && (
            <>
              <CardHeader>
                <CardTitle className="text-primary text-center">Forgot Password</CardTitle>
                <CardDescription className="text-center">
                  Enter your email address and we'll send you a reset code
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSendCode} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (error) setError(null);
                        }}
                        className="pl-10 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {step === 'code' && (
            <>
              <CardHeader>
                <CardTitle className="text-primary text-center">Enter Reset Code</CardTitle>
                <CardDescription className="text-center">
                  We sent a 6-digit code to {email}
                  {generatedCode && (
                    <div className="mt-2 p-2 bg-accent/20 rounded text-accent-foreground">
                      <strong>Demo Code:</strong> {generatedCode}
                    </div>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Reset Code</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="6-digit code"
                      value={resetCode}
                      onChange={(e) => {
                        setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        if (error) setError(null);
                      }}
                      className="rounded-xl text-center tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (error) setError(null);
                        }}
                        className="pl-10 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (error) setError(null);
                        }}
                        className="pl-10 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>

                  <Button
                    type="button"
                    variant="link"
                    className="w-full text-primary"
                    onClick={() => setStep('email')}
                  >
                    Didn't receive code? Try again
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {step === 'success' && (
            <>
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                </div>
                <CardTitle className="text-primary text-center">Password Reset Complete!</CardTitle>
                <CardDescription className="text-center">
                  Your password has been successfully reset
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={onBackToLogin}
                  className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                >
                  Back to Login
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
